import { IPublisher } from '~/src/dash/api/console-api';
import { getSelectedMetricIDFromMenuList } from '~/src/dash/api/monitors/monitors_utils';
import { checkURL } from '~/src/dash/components/vast-editor';
import { getResellerTitle } from '~/src/dash/resellers/utils/importResellerConfig';
import { isPBSDomain } from '~/src/dash/resellers/utils/utils';
import { isStringKnownBidderType } from '~/src/dash/utils/headerbidder-params';

export interface IObject {
  [key: string]: any;
}

export interface IErrors {
  [key: string]: string;
}

export interface IValidator {
  [key: string]: (v: any, key: string, obj: IObject) => string;
}

export interface ICustomValidator {
  [key: string]: (v: any, key: string, obj: IObject) => IErrors;
}

export function transformErrors(err: IErrors): { [key: string]: string[] } {
  const e: { [key: string]: string[] } = {};
  Object.keys(err).forEach((key: string) => {
    e[key] = [err[key]];
  });

  return e;
}

export function hasErrors(errs: IErrors): boolean {
  return Object.keys(errs)
    .map((key: string) => {
      return !!errs[key];
    })
    .reduce((r: boolean, v: boolean) => {
      return r || v;
    }, false);
}

export function validatorFactory(validators: IValidator) {
  return (obj: IObject): IErrors => {
    const errs: { [key: string]: string } = {};
    Object.keys(validators).forEach((key: string) => {
      errs[key] = validators[key](obj[key], key, obj);
    });
    return errs;
  };
}

export function customValidatorFactory(validators: ICustomValidator) {
  return (obj: IObject): IErrors => {
    let errs: { [key: string]: string } = {};
    Object.keys(validators).forEach((key: string) => {
      errs = { ...errs, ...validators[key](obj[key], key, obj) };
    });
    return errs;
  };
}

// ************************************
// ******** VALIDATION HELPERS ********
// ************************************

// extracts the url from the vastURL param
// ex. input: `"vastURL":"https://testtag.com/vast/?hb=1&app_bundle=[APP_BUNDLE]"`
// ex. output: https://testtag.com/vast/?hb=1&app_bundle=[APP_BUNDLE]
function getVastURLFromParamPair(paramPair: string): RegExpExecArray | null {
  return new RegExp('http[^"]*').exec(paramPair);
}

// ************************************
// ******* VALIDATION SETTINGS ********
// ************************************

export const bidderValidationSetting: IValidator = {
  Name(v: string): string {
    if (!v) {
      return 'Name cannot be empty';
    } else if (v === 'New Bidder Name') {
      return 'Choose a custom name';
    }
    return '';
  },
  Bidder(v: string): string {
    if (!v) {
      return 'Select a bidder';
    }
    return '';
  },
  BidFloor(v: string): string {
    if (!!v && v.indexOf(',') > -1) {
      return 'Please use . instead of , for decimal';
    } else if (!!v && isNaN(parseFloat(v))) {
      return 'Please input a valid decimal';
    } else if (!!v && parseFloat(v) < 0) {
      return 'Bid Floor must be $0 or higher (Leave empty if $0)';
    }
    return '';
  },
  BidAdjustmentFactor(v: number): string {
    if (v === undefined || v === null) {
      return '';
    }

    if (v > 200) {
      return 'Bid Adjustment cannot be more than 200%';
    }

    const vString = v.toString();

    if (!!vString && vString.indexOf(',') > -1) {
      return 'Please use . instead of , for decimal';
    } else if (!!vString && isNaN(parseFloat(vString))) {
      return 'Please input a valid decimal';
    }
    return '';
  },
  BidAdjustmentValue(v: number): string {
    // if value is undefined → bypass the check
    if (v === undefined || v === null) {
      return '';
    }
    // check if value is a positive number
    if (v < 0) {
      return 'Bid Adjustment Value cannot be negative';
    }
    // all test passed → OK
    return '';
  },
  FrequencyCap(v: string): string {
    if (!!v && isNaN(parseInt(v, 10))) {
      return 'Please input a valid number';
    } else if (!!v && v.split('.').length > 1) {
      return 'Please input a valid integer, not a decimal';
    } else if (parseInt(v, 10) > 0 && parseInt(v, 10) < 10000) {
      return 'Daily Request Cap must be greater or equal 10k';
    }
    return '';
  },
  MaxAdDurationOverride(v: string): string {
    // note: maxadduration will sometimes enter this function as a number
    if (v === '' || v === undefined) {
      return '';
    }
    const nbr = Number(v);
    if (isNaN(nbr)) {
      return 'Please input a valid number';
    }
    if (nbr < 0) {
      return 'Please input a positive number';
    }
    return '';
  },
  MinAdDurationOverride(v: string): string {
    // note: minadduration will sometimes enter this function as a number
    if (v === '' || v === undefined) {
      return '';
    }
    const nbr = Number(v);
    if (isNaN(nbr)) {
      return 'Please input a valid number';
    }
    if (nbr < 0) {
      return 'Please input a positive number';
    }
    return '';
  },
  StartDelayOverride(v: string): string {
    if (v === '' || v === undefined) {
      return '';
    }
    const parsedV = parseInt(v, 10);
    if (isNaN(parsedV)) {
      return 'Please input a valid number';
    }
    if (parsedV < -2) {
      return 'Please input a number >= -2';
    }
    return '';
  },
  TmaxOffset(v: string): string {
    if (v === undefined || v === '') {
      return 'TmaxOffset must have a value greater than or equal to 0';
    }
    const parsedTmaxOffset = parseInt(v, 10);
    if (isNaN(parsedTmaxOffset)) {
      return 'TmaxOffset must be a valid number';
    } else if (parsedTmaxOffset < 0 || parsedTmaxOffset > 1000) {
      return 'TmaxOffset must be between 0 and 1000';
    }
    return '';
  },
  Params(v: string): string {
    const errorFields = [];
    const paramPairs = v.split(',\n');
    for (let i = 0; i < paramPairs.length; i++) {
      // We only split on first occurrence of : so that we don't split up VAST url: https://stackoverflow.com/questions/4607745/split-string-only-on-first-instance-of-specified-character
      const paramPair = paramPairs[i].split(/:(.+)/);
      if (paramPair.length < 2) {
        continue;
      }
      if (paramPair.length > 65535) {
        return `Bidder params total cannot exceed 65,535 characters`;
      }
      const [paramKey, paramVal] = [paramPair[0], paramPair[1]];
      // If vast url, validate string against Regex
      if (paramKey.includes('vastURL')) {
        const matches = getVastURLFromParamPair(paramPairs[i]);
        if (matches && !checkURL.test(matches[0])) {
          errorFields.push('vastURL');
        }
      } else if (!paramKey.includes('keyvalue')) {
        if (paramVal.trim().split(' ').length > 1) {
          errorFields.push(`${paramKey.slice(1, paramKey.length - 1)}`);
        }
      } else if (paramKey.includes('root-keyvalue-')) {
        const prefixedKey = paramKey.slice(15, paramKey.length - 1);
        const hyphenIdx = prefixedKey.indexOf('-');
        if (hyphenIdx === -1) {
          errorFields.push(`${paramKey.slice(1, paramKey.length - 1)}`);
          continue;
        }

        const prefix = prefixedKey.slice(0, hyphenIdx);
        const key = prefixedKey.slice(hyphenIdx + 1);
        if (prefix === '' || key === '') {
          errorFields.push(`${paramKey.slice(6, paramKey.length - 1)}`);
          continue;
        }
      }
    }
    return errorFields.length ? `Malformed input detected in fields ${errorFields.join(', ')}` : '';
  },
};

export const channelListValidationSetting: IValidator = {
  channelListName(v: string): string {
    if (!v) {
      return 'Name cannot be empty';
    }
    return '';
  },
};

export const channelEditListValidationSetting: IValidator = {
  editChannelListName(v: string): string {
    if (!v) {
      return 'Name cannot be empty';
    }
    return '';
  },
};

export const bulkUpdateBiddersValidationSetting: IValidator = {
  BidFloor(v: string): string {
    if (!!v && v.indexOf(',') > -1) {
      return 'Please use . instead of , for decimal';
    } else if (!!v && isNaN(parseFloat(v))) {
      return 'Please input a valid decimal';
    }
    return '';
  },
  BidAdjustmentFactor(v: number): string {
    if (v === undefined || v === null) {
      return '';
    }

    if (v > 200) {
      return 'Bid Adjustment cannot be more than 200%';
    }

    const vString = v.toString();

    if (!!vString && vString.indexOf(',') > -1) {
      return 'Please use . instead of , for decimal';
    } else if (!!vString && isNaN(parseFloat(vString))) {
      return 'Please input a valid decimal';
    }
    return '';
  },
  BidAdjustmentValue(v: number): string {
    // if value is undefined → bypass the check
    if (v === undefined || v === null) {
      return '';
    }
    // check if value is a positive number
    if (v < 0) {
      return 'Bid Adjustment Value cannot be negative';
    }
    // all test passed → OK
    return '';
  },
  FrequencyCap(v: string): string {
    if (!!v && (isNaN(parseInt(v, 10)) || parseInt(v, 10) < 0 || v.split('.').length > 1)) {
      return 'Daily Request Cap must be a valid, positive integer';
    } else if (parseInt(v, 10) > 0 && parseInt(v, 10) < 10000) {
      return 'Daily Request Cap must be greater or equal 10k';
    }
    return '';
  },
  MaxAdDurationOverride(v: string): string {
    // note: maxadduration will sometimes enter this function as a number
    if (v === '' || v === undefined) {
      return '';
    }
    const nbr = Number(v);
    if (isNaN(nbr)) {
      return 'Please input a valid number';
    }
    if (nbr < 0) {
      return 'Please input a positive number';
    }
    return '';
  },
  MinAdDurationOverride(v: string): string {
    // note: minadduration will sometimes enter this function as a number
    if (v === '' || v === undefined) {
      return '';
    }
    const nbr = Number(v);
    if (isNaN(nbr)) {
      return 'Please input a valid number';
    }
    if (nbr < 0) {
      return 'Please input a positive number';
    }
    return '';
  },
  StartDelayOverride(v: string): string {
    if (v === '' || v === undefined) {
      return '';
    }
    const parsedV = parseInt(v, 10);
    if (isNaN(parsedV)) {
      return 'Please input a valid number';
    }
    if (parsedV < -2) {
      return 'Please input a number >= -2';
    }
    return '';
  },
};

export const regionValidationSetting: IValidator = {
  createEditRegionName(v: string): string {
    if (!v) {
      return 'Please input a valid region name';
    } else if (!!v && v.length > 50) {
      return 'Region name must be less than 50 characters long';
    } else if (!!v && v.indexOf(',') > -1) {
      return 'Region name cannot include commas';
    } else if (!!v && (v.indexOf("'") > -1 || v.indexOf('"') > -1)) {
      return 'Region name cannot include quotes';
    } else if (!!v && (v.indexOf('/') > -1 || v.indexOf('\\') > -1)) {
      return 'Region name cannot include slashes';
    }
    return '';
  },
};

export const customMetricValidatorSetting: IValidator = {
  createEditCustomMetricName(v: string, key: string, state: any): string {
    let isUniqueName = true;
    const newCustomMetricName = state[key].toLocaleLowerCase();
    for (const customMetric of state.monitor.monitorCustomMetrics) {
      // if the name exists and if not edititing existing
      if (
        customMetric.Name.toLocaleLowerCase() === newCustomMetricName &&
        !state.createEditCustomMetricID
      ) {
        isUniqueName = false;
        break;
      }
    }
    if (!v) {
      return 'Please input a valid Custom Metric name';
    } else if (!isUniqueName) {
      return 'Please input a unique Custom Metric name';
    } else if (!!v && v.length > 50) {
      return 'Custom Metric name must be less than 50 characters long';
    } else if (!!v && v.indexOf(',') > -1) {
      return 'Custom Metric name cannot include commas';
    } else if (!!v && (v.indexOf("'") > -1 || v.indexOf('"') > -1)) {
      return 'Custom Metric name cannot include quotes';
    } else if (!!v && (v.indexOf('/') > -1 || v.indexOf('\\') > -1)) {
      return 'Custom Metric name cannot include slashes';
    }
    return '';
  },
  createEditCustomMetricAList(customMetricAList: any, key: string, state: any): string {
    const selectedMetricFromListA = getSelectedMetricIDFromMenuList(customMetricAList);
    if (!state[key]) {
      return '';
    } else if (!selectedMetricFromListA) {
      return 'Please select a Metric A Value from the dropdown (Metric A is required, Metric B is optional)';
    }
    return '';
  },
  createEditCustomMetricFormula(customMetricFormula: any, key: string, state: any): string {
    const selectedMetricFromListA = getSelectedMetricIDFromMenuList(
      state.createEditCustomMetricAList,
    );
    const selectedMetricFromListB = getSelectedMetricIDFromMenuList(
      state.createEditCustomMetricBList,
    );
    if (!customMetricFormula) {
      return 'Please enter a Custom Metric Formula';
    } else if (state.customMetricFormulaError) {
      return `Please enter a valid formula before saving Custom Metric: ${state.customMetricValidationError}`;
    } else if (state[key].includes('a') && !selectedMetricFromListA) {
      return 'Cannot use var "a" because it has not been selected in Metric A dropdown';
    } else if (state[key].includes('b') && !selectedMetricFromListB) {
      return 'Cannot use var "b" because it has not been selected in Metric B dropdown';
    }
    return '';
  },
};

export const publicaBidderValidationSetting: ICustomValidator = {
  Params(v: string): IErrors {
    const lines = v.split('\n');
    const errs: { [key: string]: string } = {};
    // For each lines
    for (let i = 0; i < lines.length; i++) {
      // We only split on first occurrence of : so that we don't split up VAST url: https://stackoverflow.com/questions/4607745/split-string-only-on-first-instance-of-specified-character
      const params = lines[i].split(/:(.+)/);
      if (params.length < 2) {
        continue;
      }
      // Get and sanitize parameters
      const name = params[0].replace(/("|')/g, '');
      const value = params[1].replace(/("|,)/g, '');
      // Check if value is mandatory
      if (['price', 'vastURL'].includes(name) && value === '') {
        errs['ParamsOptions_' + name] = `Please provide a value for ${name}`;
      } else if (['vastURL'].includes(name)) {
        if (value.toLowerCase().includes('[site_id]') && isPBSDomain(value)) {
          errs[
            'ParamsOptions_' + name
          ] = `The [SITE_ID] macro is not allowed for Other Vast Tag bidders targeting a ${getResellerTitle()} endpoint.`;
        }
      }
    }
    return errs;
  },
  UIBidderType(value: string, _key: string, publisher: IPublisher): IErrors {
    // Check if UIBidderType matches a known bidder type name (ex: 'spotx').
    if (value && isStringKnownBidderType(value.trim(), publisher)) {
      return { UIBidderType: 'This name is a known demand source type. Please enter another one.' };
    }
    return {};
  },
};

export const basePlatformConnectionValidationSetting: ICustomValidator = {
  Params(v: string): IErrors {
    const lines = v.split('\n');
    const errs: { [key: string]: string } = {};
    // For each lines
    for (let i = 0; i < lines.length; i++) {
      const params = lines[i].split(':');
      if (params.length < 2) {
        continue;
      }
      // Get and sanitize parameters
      const name = params[0].replace(/("|')/g, '');
      const value = params[1].replace(/("|,)/g, '');
      // Check if value is mandatory
      if (
        ['siteID', 'globalTimeoutOffset'].includes(name) &&
        (value === '' || value === undefined)
      ) {
        errs['ParamsOptions_' + name] = 'Please provide a value';
      }
      // Check if site id is integer
      if (['siteID'].includes(name) && !Number.isInteger(parseInt(value, 10))) {
        errs['ParamsOptions_' + name] = 'Site ID must be an integer';
      }
    }
    return errs;
  },
};

export const fixedPricePlatformConnectionBidderValidationSetting: ICustomValidator = {
  ...basePlatformConnectionValidationSetting,
  FixedPrice(value: number): IErrors {
    const errs: { [key: string]: string } = {};
    if (!value) {
      // ensures that the fixed price is non empty
      errs.FixedPrice = 'Please enter a fixed price';
    } else if ((String(value).match(/\d/g) || []).length < 1 || value <= 0) {
      // ensures that a fixed price is entered and > 0
      errs.FixedPrice = 'Please enter a fixed price greater than 0';
    } else if ((String(value).match(/\./g) || []).length > 1) {
      // ensures that there is only one decimal
      errs.FixedPrice = 'Please only use one . for fixed price';
    } else if (/[^\d\.]/.test(String(value))) {
      // ensures that all characters are numeric or decimal
      errs.FixedPrice = 'Please input a valid price';
    }
    return errs;
  },
};

export const rokuHBBidderValidationSetting: ICustomValidator = {
  Params(v: string): IErrors {
    const lines = v.split('\n');
    const errs: { [key: string]: string } = {};
    // track which fields are empty
    const missingFields = {
      index: true,
      subDomain: true,
      seatId: true,
      tagId: true,
    };
    // For each lines
    for (let i = 0; i < lines.length; i++) {
      const params = lines[i].split(':');
      if (params.length < 2) {
        continue;
      }
      // Get and sanitize parameters
      const name = params[0].replace(/("|')/g, '');
      const value = params[1].replace(/("|,)/g, '');
      // Check mandatory values
      if (name === 'index') {
        missingFields.index = false;
        if (value === '' || value === undefined) {
          errs['ParamsOptions_' + name] = 'Please provide a value';
        }
        // Check if index is 1 (HB-3060 => switch to single index)
        const index = Number(value);
        if (index === undefined || !Number.isInteger(index) || index !== 1) {
          errs['ParamsOptions_' + name] = 'Index must be 1';
        }
      }
      if (name === 'subDomain') {
        missingFields.subDomain = false;
      }
      if (name === 'seatId') {
        missingFields.seatId = false;
      }
      if (name === 'tagId') {
        missingFields.tagId = false;
      }
    }
    if (missingFields.index) {
      errs['ParamsOptions_' + 'index'] = 'Please provide a value';
    }
    if (missingFields.subDomain) {
      errs['ParamsOptions_' + 'subDomain'] = 'Please provide a value';
    }
    if (missingFields.seatId) {
      errs['ParamsOptions_' + 'seatId'] = 'Please provide a value';
    }
    if (missingFields.tagId) {
      errs['ParamsOptions_' + 'tagId'] = 'Please provide a value';
    }
    return errs;
  },
};

export const rokuORTBBidderValidationSetting: ICustomValidator = {
  Params(v: string): IErrors {
    const lines = v.split('\n');
    const errs: { [key: string]: string } = {};
    // track which fields are empty
    const missingFields = {
      subDomain: true,
      seatId: true,
      tagId: true,
      publisherId: true,
    };
    // For each lines
    for (let i = 0; i < lines.length; i++) {
      const params = lines[i].split(':');
      if (params.length < 2) {
        continue;
      }
      // Get and sanitize parameters
      const name = params[0].replace(/("|')/g, '');
      // Check mandatory values
      if (name === 'subDomain') {
        missingFields.subDomain = false;
      }
      if (name === 'seatId') {
        missingFields.seatId = false;
      }
      if (name === 'tagId') {
        missingFields.tagId = false;
      }
      if (name === 'publisherId') {
        missingFields.publisherId = false;
      }
    }
    if (missingFields.subDomain) {
      errs['ParamsOptions_' + 'subDomain'] = 'Please provide a value';
    }
    if (missingFields.seatId) {
      errs['ParamsOptions_' + 'seatId'] = 'Please provide a value';
    }
    if (missingFields.tagId) {
      errs['ParamsOptions_' + 'tagId'] = 'Please provide a value';
    }
    if (missingFields.publisherId) {
      errs['ParamsOptions_' + 'publisherId'] = 'Please provide a value';
    }
    return errs;
  },
};

export const apsOpenAuctionValidationSettings: ICustomValidator = {
  Params(v: string): IErrors {
    const lines = v.split('\n');
    const errs: { [key: string]: string } = {};
    // For each lines
    let hasPriceRange = false;
    for (let i = 0; i < lines.length; i++) {
      const params = lines[i].split(':');
      if (params.length < 2) {
        continue;
      }
      // Get and sanitize parameters
      const name = params[0].replace(/("|')/g, '');
      const value = params[1].replace(/("|,)/g, '');
      if (name === 'price_range') {
        if (value === '' || value === undefined) {
          errs['ParamsOptions_' + name] = 'Please choose a price range';
        } else {
          hasPriceRange = true;
        }
      }
    }
    if (!hasPriceRange) {
      errs.ParamsOptions_price_range = 'Please choose a price range';
    }
    return errs;
  },
};

export const apsPMPValidationSettings: ICustomValidator = {
  Params(v: string): IErrors {
    const lines = v.split('\n');
    const errs: { [key: string]: string } = {};
    // track which fields are empty
    const missingFields = {
      price: true,
      targeting_value: true,
    };
    // For each lines
    for (let i = 0; i < lines.length; i++) {
      const params = lines[i].split(':');
      if (params.length < 2) {
        continue;
      }
      // Get and sanitize parameters
      const name = params[0].replace(/("|')/g, '');
      const value = params[1].replace(/("|,)/g, '');
      // Check if price is number
      if (name === 'price') {
        missingFields.price = false;
        if (value === '' || value === undefined) {
          errs['ParamsOptions_' + name] = 'Please provide a value';
        }
        // @ts-ignore
        if (isNaN(value)) {
          errs['ParamsOptions_' + name] = 'Price must be a number';
          // @ts-ignore
        } else if (!isNaN(value) && parseFloat(value) < 0) {
          errs['ParamsOptions_' + name] = 'Price must be a positive number';
        }
      }
      if (name === 'targeting_value') {
        missingFields.targeting_value = false;
        if (value === '' || value === undefined) {
          errs['ParamsOptions_' + name] = 'Please provide a key';
        }
        if (value.length < 3 || value.slice(0, 3) !== 'aps') {
          errs['ParamsOptions_' + name] = 'Key must start with "aps"';
        }
      }
    }
    if (missingFields.price) {
      errs['ParamsOptions_' + 'price'] = 'Please provide a value';
    }
    if (missingFields.targeting_value) {
      errs['ParamsOptions_' + 'targeting_value'] = 'Please provide a value';
    }
    return errs;
  },
};

export const apsGuaranteeValidationSettings: ICustomValidator = {
  Params(v: string): IErrors {
    const lines = v.split('\n');
    const errs: { [key: string]: string } = {};
    // For each lines
    for (let i = 0; i < lines.length; i++) {
      const params = lines[i].split(':');
      if (params.length < 2) {
        continue;
      }
      // Get and sanitize parameters
      const name = params[0].replace(/("|')/g, '');
      const value = params[1].replace(/("|,)/g, '');
      if (name === 'inv') {
        if (value === '' || value === undefined) {
          errs['ParamsOptions_' + name] = 'Please provide an "inv" value';
        }
      }
    }
    return errs;
  },
};
